import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styles from './search_result_commentary_sources.module.scss';
import { SearchCommentary } from './searchResultComment.types';
import { Hyperlink } from '../../../../components/Hyperlink';
import { useCommentaryDecisionSourcesModal } from '../../../../commentary/components/sources-modal/useCommentaryDecisionSourcesModal';
import { doctrineAnalytics } from '../../../../utils/analyticsUtils';
import { camelToSnake } from '../../../../caseUtils';
import { useCommentaryLegislationSourcesModal } from '../../../../commentary/components/sources-modal/useCommentaryLegislationSourcesModal';

type Props = {
  commentary: SearchCommentary;
  source: string;
};

export const SearchResultCommentarySources: FC<Props> = ({
  commentary,
  source,
}) => {
  const modalParams = {
    commentaryId: commentary.referenceCommentaryId,
    source,
    trackingOnOpen: 'Search ModalCommentarySourcesOpened',
    trackingOnClick: 'Search ModalCommentarySourceClicked',
  };
  const openDecisionSourcesModal = useCommentaryDecisionSourcesModal({
    ...modalParams,
  });
  const openLegislationSourcesModal = useCommentaryLegislationSourcesModal({
    ...modalParams,
  });

  const onDecisionSourceClicked = () => {
    doctrineAnalytics('Search CommentaryDecisionClicked', {
      ...camelToSnake(commentary),
      reportOriginalQueryKey: true,
    });
  };

  const onLegislationSourceClicked = () => {
    doctrineAnalytics('Search CommentaryLegislationClicked', {
      ...camelToSnake(commentary),
      reportOriginalQueryKey: true,
    });
  };

  const {
    documents: decisionDocuments,
    total: decisionTotal,
  } = commentary.linkedDecisions;

  const {
    documents: legislationDocuments,
    total: legislationTotal,
  } = commentary.linkedLegislationArticles;

  const hasOneDecision = decisionTotal === 1 && legislationTotal === 0;
  const hasOneLegislation = legislationTotal === 1 && decisionTotal === 0;
  const hasMultipleSources = !hasOneDecision && !hasOneLegislation;

  return (
    <div className={styles.container}>
      {hasOneDecision && (
        <Hyperlink
          href={decisionDocuments[0].url}
          target="_blank"
          source={source}
          onClick={onDecisionSourceClicked}
          className={styles.link}
        >
          {decisionDocuments[0].title}
        </Hyperlink>
      )}
      {hasOneLegislation && (
        <Hyperlink
          href={legislationDocuments[0].url}
          target="_blank"
          source={source}
          onClick={onLegislationSourceClicked}
          className={styles.link}
        >
          {legislationDocuments[0].title}
        </Hyperlink>
      )}
      {hasMultipleSources && (
        <>
          {decisionTotal > 0 && (
            <button
              type="button"
              className={styles.link}
              onClick={openDecisionSourcesModal}
            >
              <FormattedMessage
                id="search/SearchResultCommentarySources.tsx/1"
                values={{ count: decisionTotal }}
              />
            </button>
          )}
          {decisionTotal > 0 && legislationTotal > 0 && (
            <span className={styles.separator}>·</span>
          )}
          {legislationTotal > 0 && (
            <button
              type="button"
              className={styles.link}
              onClick={openLegislationSourcesModal}
            >
              <FormattedMessage
                id="search/SearchResultCommentarySources.tsx/2"
                values={{ count: legislationTotal }}
              />
            </button>
          )}
        </>
      )}
    </div>
  );
};
